import * as React from 'react';
import './CreateProjectPage.css'
import { Helmet } from 'react-helmet-async';
import { useRef, useState, useEffect } from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
// @mui
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Container, Stack, Typography, Card, InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import { Toast } from "primereact/toast";
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import { Theme, useTheme } from '@mui/material/styles';
import { Link } from "react-router-dom";
import Iconify from '../components/iconify';
import Input from '../theme/overrides/Input';
import useInsertProject from '../hooks/Projects/useInsertProject';
import useUpdateProject from '../hooks/Projects/useUpdateProject';
import useCreateKB from '../hooks/Projects/useCreateKB';
// ----------------------------------------------------------------------

/**
 * Dropdown option interface
 */
interface dropdown {
  label: string;
  value: string;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

/**
 * Get styles for selected users
 * @param name - User name
 * @param selectedUsers - Array of selected users
 * @param theme - MUI theme
 * @returns Object containing styles for selected users
 */
function getStyles(name: string, selectedUsers: readonly string[], theme: Theme) {
  return {
    fontWeight:
      selectedUsers.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

/**
 * CreateProjectPage component
 * @returns JSX.Element
 */
export default function CreateProjectPage() {

  // State for form fields
  const [projectName, setProjectName] = useState('');
  const [users, setUsers] = useState<dropdown[]>([]);
  const [selectedUsers, setselectedUsers] = React.useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [exampleQuestion, setExampleQuestion] = useState('');
  const [exampleQuestions, setExampleQuestions] = useState(['']);
  const [systemPrompt, setSystemPrompt] = useState('');
  const toast = useRef<Toast>(null);
  var project_index_mapping = null
  const theme = useTheme();

  const { data: insertProjectData, loading: insertProjectLoading, insertProject } = useInsertProject();
  const { data: updateProjectData, loading: updateProjectLoading, updateProject } = useUpdateProject();
  const { data: createKBData, loading: createKBLoading, createKB } = useCreateKB();
  useEffect(() => {
    // Fetch users data from the server
    fetchUsers();
  }, []); // Empty dependency array ensures the effect runs once when the component mounts

  /**
   * Fetch users data from the server
   */
  const fetchUsers = async () => {
    try {
      // Replace 'your-api-endpoint' with the actual API endpoint that fetches users from your server
      const response = await fetch(process.env.REACT_APP_LIST_USERS_URL || '');
      const data = await response.json();
      // Assuming the response is an array of objects with 'label' and 'value' properties
      const usersData: dropdown[] = data.map((user: any) => ({
        label: user.name,
        value: user.email,
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Handle error or show a toast message
      showError("Error in Creating Project. Please try again.")
    }
  };

  /**
   * Handle user change event
   * @param event - SelectChangeEvent
   */
  const handleUserChange = (event: SelectChangeEvent<typeof selectedUsers>) => {
    const {
      target: { value },
    } = event;
    setselectedUsers(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  /**
   * Handle add example question event
   */
  const handleAddExampleQuestion = () => {
    if (exampleQuestions.length < 3) {
      setExampleQuestions([...exampleQuestions, '']);
    }
  };

  /**
   * Handle remove example question event
   * @param index - Index of the example question to be removed
   */
  const handleRemoveExampleQuestion = (index: number) => {
    const updatedExampleQuestions = [...exampleQuestions];
    updatedExampleQuestions.splice(index, 1);
    setExampleQuestions(updatedExampleQuestions);
  };

  /**
   * Handle example question change event
   * @param index - Index of the example question
   * @param value - New value of the example question
   */
  const handleExampleQuestionChange = (index: number, value: string) => {
    const updatedExampleQuestions = [...exampleQuestions];
    updatedExampleQuestions[index] = value;
    setExampleQuestions(updatedExampleQuestions);
  };

  const departmentName: dropdown[] = [
    { label: "Big Data", value: "Big Data" },
    { label: "AI/ML", value: "AI/ML" },
    { label: "Salesforce", value: "Salesforce" },
  ];

  const [selectedDepartment, setSelectedDepartment] = React.useState(departmentName[0].value);

  /**
   * Handle department change event
   * @param event - SelectChangeEvent
   */
  const handleChange = (event: SelectChangeEvent) => {
    setSelectedDepartment(event.target.value as string);
  };

  /**
   * Show success toast message
   * @param text - Toast message text
   */
  const showSuccess = (text: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: text, life: 3000 });
  }

  /**
   * Show info toast message
   * @param text - Toast message text
   */
  const showInfo = (text: string) => {
    toast.current?.show({ severity: 'info', summary: 'Info', detail: text, life: 3000 });
  }

  /**
   * Show warning toast message
   * @param text - Toast message text
   */
  const showWarn = (text: string) => {
    toast.current?.show({ severity: 'warn', summary: 'Warning', detail: text, life: 3000 });
  }

  /**
   * Show error toast message
   * @param text - Toast message text
   */
  const showError = (text: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: text, life: 3000 });
  }

  /**
   * Create project
   */
  const createProject = async () => {

    // Validate form fields
    if (!projectName) {
      showError("Please enter project name");
      return;
    }
    if (!budget) {
      showError("Please enter budget");
      return;
    }
    if (!exampleQuestions.every((question) => question)) {
      showError("Please enter all example questions");
      return;
    }
    if (!systemPrompt) {
      showError("Please enter system prompt");
      return;
    }
    if (selectedUsers.length === 0) {
      showError("Please select users");
      return;
    }
    if (!selectedDepartment) {
      showError("Please select department");
      return;
    }

    // Prepare request object
    const requestObject = {
      proj_name: projectName,
      Users: selectedUsers,
      dept_name: selectedDepartment,
      budget: budget,
      examplequestion: exampleQuestions,
      systemprompt: systemPrompt,
      embeddingModel: "amazon.titan-embed-text-v1",
      app_name: "PMR",
      role: "user"
    };
    insertProject(requestObject);
  }


  useEffect(() => {
    if (insertProjectData) {
      if (insertProjectData =="Project created successfully") {
        // project_index_mapping = JSON.parse(insertProjectData.body)
        // console.log(project_index_mapping)
        // if (project_index_mapping.project_status === "Project name already exist") {
        //   showError("Project name already exist. Please try again.")
        //   return
        // }
        // showInfo("Project created successfully");
        showSuccess("Project created successfully!")
        // Update project

        // project_index_mapping = JSON.parse(insertProjectData.body)
        // const createKBRequestObject = {
        //   proj_name: project_index_mapping.project_name,
        //   index_name: project_index_mapping.index_name,
        //   kb_name: project_index_mapping.kb_name,
        //   ds_name: project_index_mapping.ds_name,
        //   s3_bucket_name: project_index_mapping.s3_bucket_name,
        //   embeddingModel: "amazon.titan-embed-text-v1",
        //   project_status: project_index_mapping.project_status
        // };
        // createKB(createKBRequestObject);

      } 
      else if (insertProjectData == "Project name already exist"){
        showInfo("Project name already exist");
        showError("Project name already exist. Please try again.")
      }
      else {
        if (Object.keys(insertProjectData).length !== 0) {
          showError("Error in Creating Project Knowledge Base. Please try again.")
        }
      }
    }
  }, [insertProjectData]);

  useEffect(() => {

    if (createKBData) {
      if (createKBData.statusCode === 200) {
        showInfo("Project updated successfully");
        project_index_mapping = JSON.parse(insertProjectData.body)
        const updateRequestObject = {
          proj_name: project_index_mapping.project_name,
          index_name: project_index_mapping.index_name,
          kb_name: project_index_mapping.kb_name,
          ds_name: project_index_mapping.ds_name,
          s3_bucket_name: project_index_mapping.s3_bucket_name,
          project_status: project_index_mapping.project_status
        };
        updateProject(updateRequestObject);

      } else {
        if (Object.keys(updateProjectData).length !== 0) {
          showError("Error in Creating Knowledge Base. Please try again.")
        }
      }
    }
  }, [createKBData]);

  useEffect(() => {
    if (updateProjectData) {
      if (updateProjectData.statusCode === 200) {
        showSuccess("Updated Project successfully");
      } else {
        if (Object.keys(updateProjectData).length !== 0) {
          showError("Error in updating Project. Please try again.")
        }
      }
    }
  }, [updateProjectData]);

  return (
    <>
      <Toast ref={toast} />
      <Helmet>
        <title> Create New Project </title>
      </Helmet>

      <Container>
        <Typography variant="h4" mb={3} mt={4}
         sx={{
          color: theme.palette.mode === 'dark' ? '#C7C9C7' : '#333f48',
        }}>
          Create New Project
        </Typography>
        <Card sx={{ p: 2 }}>
          <div className="select-container">
            <div>
              <FormControl sx={{ m: 1, minWidth: '100%' }}>
                <label htmlFor="projectname" style={{ fontWeight: "bold" }}>
                  Select Department
                </label>
                <Select
                  labelId="demo-simple-select-autowidth-label"
                  id="demo-simple-select-autowidth"
                  value={selectedDepartment}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departmentName.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <FormControl sx={{ m: 1, width: '100%' }}>
                <label htmlFor="selectproject" style={{ fontWeight: "bold" }}>
                  Select Users
                </label>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={selectedUsers}
                  onChange={handleUserChange}
                  input={<OutlinedInput id="select-multiple-chip" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {users.map((item) => (
                    <MenuItem key={item.value} value={item.value} style={getStyles(item.label, selectedUsers, theme)}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className="card flex justify-content-center">
            <div style={{ display: 'flex', gap: '20px' }}>
              <FormControl sx={{ m: 1, flex: 1 }}>
                <label htmlFor="projectname" style={{ fontWeight: 'bold' }}>
                  Name
                </label>
                <TextField
                  placeholder='Enter Project Name'
                  id="projectname"
                  value={projectName}
                  variant="outlined"
                  onChange={(e) => setProjectName(e.target.value)}
                />
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }}>
                <label htmlFor="budget" style={{ fontWeight: 'bold' }}>
                  Monthly Budget
                </label>
                <TextField
                  id="budget"
                  value={budget}
                  variant="outlined"
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder='Enter Amount'
                  type='number'
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </FormControl>
            </div>
          </div>

          <div className="card flex justify-content-center">
            {exampleQuestions.map((exampleQuestion, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>

                <FormControl sx={{ m: 1, flex: 1 }}>
                  <label htmlFor={`examplequestion${index}`} style={{ fontWeight: 'bold' }}>
                    Example Question #{index + 1}
                  </label>
                  <TextField
                    id={`examplequestion${index}`}
                    value={exampleQuestion}
                    variant="outlined"
                    onChange={(e) => handleExampleQuestionChange(index, e.target.value)}
                  />
                </FormControl >

                <div className="Qnbtn-container">
                  {exampleQuestions.length > 1 && (
                    <Button variant="text" onClick={() => handleRemoveExampleQuestion(index)} size="small" style={{ minWidth: 100, marginLeft: 16 }}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {exampleQuestions.length < 3 && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddExampleQuestion}
                size="small"
                style={{ minWidth: 100, marginLeft: 16 }}
              >
                + Add Example Question
              </Button>
            )}
          </div>
          <br />

          <div className="card flex justify-content-center" style={{ display: 'flex' }}>
            <FormControl sx={{ m: 1 }}>
              <label htmlFor="projectname" style={{ fontWeight: 'bold' }}>
                System Prompt
              </label>
              <TextField
                id="systemprompt"
                value={systemPrompt}
                variant="outlined"
                onChange={(e) => setSystemPrompt(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ m: 1, minWidth: 700 }}>
              <label style={{ fontWeight: 'bold' }}>
                Example
              </label>
              <Typography variant="body1">
                {/* Your example text */}
                System Assistant helps the users with questions about roofing systems warranty manuals. Be brief in your answers.Answer ONLY with the facts listed in the list of sources below. If there isn"t enough information below, say you don"t know. Do not generate answers that don"t use the sources below. If asking a clarifying question to the user would help, ask the question.For tabular information return it as an html table. Do not return markdown format.Each source has a name followed by colon and the actual information, always include the source name for each fact you use in the response. Use square brakets to reference the source, e.g. [info1.txt]. Don"t combine sources, list each source separately, e.g.
              </Typography>
            </FormControl>
          </div>

          <FormControl sx={{ m: 1, minWidth: 250 }}>
            <div>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  className='primary-btn'
                  onClick={async () => await createProject()}>
                  Create
                </Button>
                <Link to='/dashboard/project'>
                  {/* @ts-ignore */}
                  <Button variant="contained" size="large" className='primary-btn'>
                    Back
                  </Button>
                </Link>
              </Stack>
            </div>
          </FormControl>
        </Card>
      </Container>
    </>
  );
}
